import React, { useContext, useState, useEffect } from 'react';
import { ChevronLeft, AlertCircle, Loader2, Building2, Calendar, CreditCard, Edit, History, User, Clock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import subscriptionServices from '../../../../services/subscriptionServices';

export default function SubscriptionDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    if (id) {
      fetchSubscriptionDetails();
    }
  }, [id]);

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await subscriptionServices.getSubscriptionDetails(authTokens.access, { id });
      
      if (response.success) {
        // Handle nested response structure
        const subscriptionData = {
          ...response.data.subscription,
          organization: response.data.organization,
          recent_history: response.data.recent_history
        };
        setSubscription(subscriptionData);
      } else {
        setError(response.error || 'Erreur lors du chargement des détails de l\'abonnement');
      }
    } catch (err) {
      setError('Erreur de connexion lors du chargement des détails');
      console.error('Error fetching subscription details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/admin-panel/subscriptions/${id}/update`);
  };

  const handleViewHistory = () => {
    if (subscription?.organization?.id) {
      navigate(`/admin-panel/organizations/${subscription.organization.id}/subscription-history`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Actif' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', label: 'Expiré' },
      suspended: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Suspendu' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Annulé' }
    };

    const config = statusConfig[status] || statusConfig.cancelled;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Chargement des détails...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Erreur</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="text-gray-500">Abonnement non trouvé</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Détails de l'abonnement</h1>
            <p className="text-sm text-gray-400 mt-1">
              Informations complètes sur l'abonnement
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleViewHistory}
            className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <History className="h-4 w-4" />
            <span>Historique</span>
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>Modifier</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Organization Info */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-green-600 mr-2" />
              <h2 className="text-md font-semibold text-gray-900">Organisation</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <p className="text-sm text-gray-900">{subscription.organization?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-sm text-gray-900">{subscription.organization?.description || '-'}</p>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <CreditCard className="h-5 w-5 text-green-600 mr-2" />
              <h2 className="text-md font-semibold text-gray-900">Détails de l'abonnement</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                {getStatusBadge(subscription.status)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jours restants</label>
                <p className="text-sm text-gray-900">
                  {subscription.days_remaining !== null && subscription.days_remaining !== undefined ? `${subscription.days_remaining} jours` : '-'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <p className="text-sm text-gray-900">{formatDate(subscription.start_date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <p className="text-sm text-gray-900">{formatDate(subscription.end_date)}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Raison actuelle</label>
                <p className="text-sm text-gray-900">
                  {subscription.reason || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Recent History */}
          {subscription.recent_history && subscription.recent_history.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <History className="h-5 w-5 text-orange-600 mr-2" />
                  <h2 className="text-md font-semibold text-gray-900">Historique récent</h2>
                </div>
                <button
                  onClick={handleViewHistory}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Voir tout →
                </button>
              </div>
              
              <div className="space-y-3">
                {subscription.recent_history.slice(0, 5).map((entry, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {subscriptionServices.getActionDisplayText(entry.action)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(entry.action_date)} • {entry.performed_by || 'Système'}
                      </p>
                      {entry.reason && (
                        <p className="text-xs text-gray-600 mt-1">{entry.reason}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Informations rapides</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">ID Abonnement</span>
                <span className="text-sm font-medium text-gray-900">#{subscription.id}</span>
              </div>
              
              
              {subscription.status === 'active' && subscription.days_remaining !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Progression</span>
                  <span className="text-sm font-medium text-gray-900">
                    {subscription.days_remaining > 0 ? 
                      `${Math.round((1 - subscription.days_remaining / Math.ceil((new Date(subscription.end_date) - new Date(subscription.start_date)) / (1000 * 60 * 60 * 24))) * 100)}%`
                      : '100%'
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-md font-semibold text-gray-900 mb-4">Métadonnées</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Créé par</label>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{subscription.created_by || 'Système'}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Date de création</label>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-900">{formatDate(subscription.created_at)}</span>
                </div>
              </div>
              
              {subscription.last_updated_by && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Dernière modification par</label>
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{subscription.last_updated_by}</span>
                  </div>
                </div>
              )}
              
              {subscription.updated_at && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Dernière modification</label>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{formatDate(subscription.updated_at)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
